// Centralised token storage.
//
// XSS surface reduction: the primary token lives in a module-scoped variable
// (in-memory). It's also mirrored to sessionStorage so the token survives a
// page refresh but is scoped to the current tab/session — safer than the
// previous localStorage-only strategy which persisted the token across every
// tab, window, and browser restart.
//
// We still read a legacy `aii_token` from localStorage on first load (one-time
// migration) so users signed in on the old build stay signed in.

const KEY = "aii_token";
const LEGACY_KEY = "aii_token";

let memoryToken = null;

function hasWindow() {
  return typeof window !== "undefined";
}

function readStored() {
  if (!hasWindow()) return null;
  try {
    const s = window.sessionStorage?.getItem(KEY);
    if (s) return s;
    // One-time migration from legacy localStorage.
    const legacy = window.localStorage?.getItem(LEGACY_KEY);
    if (legacy) {
      window.sessionStorage?.setItem(KEY, legacy);
      window.localStorage?.removeItem(LEGACY_KEY);
      return legacy;
    }
  } catch { /* storage disabled — fall through */ }
  return null;
}

// Hydrate memory on first import.
memoryToken = readStored();

export function getToken() {
  if (memoryToken) return memoryToken;
  memoryToken = readStored();
  return memoryToken;
}

export function setToken(token) {
  memoryToken = token || null;
  if (!hasWindow()) return;
  try {
    if (token) window.sessionStorage?.setItem(KEY, token);
    else window.sessionStorage?.removeItem(KEY);
    // Also purge any stale localStorage copy from older builds.
    window.localStorage?.removeItem(LEGACY_KEY);
  } catch { /* ignore storage errors */ }
}

export function clearToken() {
  setToken(null);
}

export function hasToken() {
  return Boolean(getToken());
}
